/**
 * ACL Sync Button
 * Shows in header when there are pending ACL configuration changes
 */

import { useState, useCallback } from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useAclConfigStore } from '../../store/aclConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { aclApi } from '../../modules/acl/api/aclApi';

export function AclSyncButton() {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const { hasPendingChanges, clearPendingChanges } = useAclConfigStore();
    const [isApplying, setIsApplying] = useState(false);

    const handleApply = useCallback(async () => {
        setIsApplying(true);

        try {
            await aclApi.sync();
            clearPendingChanges();
            showSuccess(t('acl.notifications.syncSuccess', 'ACLs synchronized to FreeSWITCH successfully'));
        } catch (error: any) {
            showError(error.message || t('acl.notifications.syncFailed', 'Failed to synchronize ACLs'));
        } finally {
            setIsApplying(false);
        }
    }, [clearPendingChanges, showSuccess, showError, t]);

    // Don't render if no pending changes
    if (!hasPendingChanges) {
        return null;
    }

    return (
        <button
            onClick={handleApply}
            disabled={isApplying}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-900 bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors animate-pulse disabled:animate-none disabled:opacity-70"
            title={t('acl.buttons.pendingChanges', 'ACL configuration changes pending - Click to apply')}
        >
            {isApplying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <ShieldAlert className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{t('acl.buttons.sync', 'Sync ACL')}</span>
        </button>
    );
}

export default AclSyncButton;
